import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getMaxPointSize } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum). 'forbidden': a night sky has
// no stage lighting.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'constellation',
  family: 'vector',
  energyLevel: 0.18,
  bpmMin: 40,
  bpmMax: 140,
  movement: 0.25,
  danceability: 0.30,
  tonality: 0.88,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.45], density: [0.0, 0.50] },
  enabled: true
};

// Constellation: a star chart drawing itself. Stars arrive on envelope rises,
// lines reach out to their nearest already-living neighbours, figures form and
// then come apart as their stars age out.
//
// THE SHAPE OF IT is accumulate-then-forget. The visual rhythm is the birth and
// erasure cycle, NOT the tempo - which is why there is no director.pulse()
// anywhere in this file. A beat pulse on top of an accumulating field would
// fight the one thing the piece is doing.
//
// EVERYTHING WITH A LIFETIME IS RESOLVED AT BIRTH, and that is the central
// implementation idea. A star knows when it was born and how long it will live;
// a line knows when it starts drawing, how long that takes, when it starts
// being erased, and from WHICH end. All of it goes into instance attributes
// once, at creation, and the shaders derive every fade, every draw front and
// every retreat from a single uTime uniform. So update() uploads nothing on a
// frame where nothing is born, allocates nothing ever, and a pause is just a
// clock that stops - including mid-draw lines, which freeze exactly where the
// front had got to.
//
// The one thing that genuinely needs CPU work is finding a new star's nearest
// neighbours, and that goes through a spatial grid - see _linkNewStar.

const CAMERA_DIST = 9.0;

// --- stars ------------------------------------------------------------------
const BIRTH_SPACING_SPARSE_MS = 300;   // character.density -> 0
const BIRTH_SPACING_DENSE_MS = 120;    // character.density -> 1
const RISE_THRESHOLD = 0.05;
const LIFE_MIN_S = 25;
const LIFE_MAX_S = 40;
const FADE_IN_S = 1.2;
// Four seconds. A star that vanishes on one frame breaks the calm, and it also
// takes its lines with it in a way the eye reads as a glitch rather than as an
// ending.
const FADE_OUT_S = 4.0;

// Band -> size, brightness and which third of the frame it lands in. The size
// SPREAD between the weak and the strong is what character.dynamics widens.
const BAND_LOW = 0, BAND_MID = 1, BAND_HIGH = 2;
const BAND_HALO_PX = [14.0, 9.0, 6.0];
const BAND_BRIGHT = [1.0, 0.82, 0.92];
const BAND_Y_RANGE = [[-1.0, -0.34], [-0.36, 0.36], [0.34, 1.0]]; // fractions of half-height
const CORE_PX = 3.0;
const SIZE_SPREAD_MIN = 0.72;  // character.dynamics -> 0: sizes bunch together
const SIZE_SPREAD_MAX = 1.28;  // -> 1: weak and strong pull apart
const TWINKLE_AMOUNT = 0.12;
const TWINKLE_RATE_MIN = 0.18;
const TWINKLE_RATE_MAX = 0.45;
// The four fine diffraction spikes go on the brightest stars only. This is the
// detail that makes the frame read as STARS rather than as bright dots, and it
// stops being that the moment everything has them. The threshold is on the
// star's own brightness and is tuned to catch about the top 15% of births -
// see the birth-mix measurement in the header of _birthStar.
// Solved, not guessed: brightness is uniform on [0.85B, 1.15B] for a band of
// weight B, and the three bands are equally likely, so the fraction clearing a
// threshold T is the mean of clamp((1.15B - T) / 0.3B). At 1.04 that is 14.4%.
// Measured over a real run it lands at 15%.
const SPIKE_BRIGHTNESS_MIN = 1.04;
const SPIKE_EXTENT = 3.2;      // spike reach, as a multiple of the halo radius
const SPIKE_STRENGTH = 0.30;

// --- placement --------------------------------------------------------------
// Best-of-K rejection sampling, maximising the distance to the nearest existing
// star. Without it births clump - two stars a pixel apart are one bright blob
// with a meaningless line between them, and the graph stops being readable.
// A real force simulation is not needed and would be worse: it would move stars
// after birth, which would drag every line they anchor.
const PLACEMENT_CANDIDATES = 12;
const PLACEMENT_GOOD_ENOUGH = 1.6; // world units - stop searching once this clear
const SAFE_ZONE_BIRTH_MARGIN = 0.04;

// --- lines ------------------------------------------------------------------
const LINK_NEIGHBOURS_MIN = 2;
const LINK_NEIGHBOURS_MAX = 3;
const LINK_MAX_DIST_FRAC = 0.42;   // of the frame half-height
const LINE_DRAW_MIN_S = 0.8;
const LINE_DRAW_MAX_S = 1.6;
const LINE_WIDTH_PX = 1.5;
// The lines are support, not subject. At full strength they turn the frame into
// a wire mesh and the stars stop carrying the image.
const LINE_BRIGHTNESS = 0.35;

// --- background -------------------------------------------------------------
const BG_STAR_PX_MIN = 1.0;
const BG_STAR_PX_MAX = 2.4;
// Raised from 0.06/0.20 after looking at a rendered frame: at that level the
// backdrop was invisible and the empty quarters of the sky read as dead black,
// which is the one thing these 400 points exist to prevent. Still an order of
// magnitude under the living stars, so they read as distance, not as clutter.
const BG_STAR_BRIGHT_MIN = 0.10;
const BG_STAR_BRIGHT_MAX = 0.34;
// THE CENTRAL WASH. Every number here was cut back after it read as a visible
// green halo with rings in it on the TV - it is meant to be a thing you cannot
// point at, only an impression that the middle of the sky is slightly less
// black than the corners.
//
// Radius is in the same units as the fragment's own p: half-height 0.5, half-
// width aspect/2. At 16:9 the frame is 1.78 wide, so a quarter of the width is
// 0.44 and this sits just inside it - previously 0.85, which reached almost the
// full half-width and swallowed half the frame.
const BG_HALO_RADIUS = 0.42;
// Cubed, not linear. A linear ramp spreads the falloff over the whole radius,
// which is both what made the wash so present AND what made it band: the
// slower a gradient moves, the wider each 8-bit plateau gets. Cubing collapses
// most of the brightness into the middle and gets out of the way quickly.
const BG_HALO_FALLOFF = 3.0;
const BG_TINT_STRENGTH = 0.028;
// Keep a fifth of the cover colour's saturation. The previous line ADDED 0.15
// to it, which is how a merely greenish cover became a frank green wash across
// the middle of the frame.
const BG_TINT_SATURATION = 0.20;
// 1%. Grain is texture, not correction - the dither below is what actually
// fixes the quantisation, and grain this strong was riding on top of the
// plateaus and advertising them rather than hiding them.
const BG_GRAIN = 0.010;

// --- the far sky (27/09/2026) ----------------------------------------------
// At the start of a composition, before the living field has grown, the
// frame was 94% pixels under 3/255 - a pure black rectangle with a few
// dots. Three far layers now sit under everything, all FIXED like the 400
// background stars (the living field turns in front of them, which reads as
// depth), all far below the living stars:
//   - a dense sprinkle of sub-pixel stars, denser inside the band;
//   - a diffuse Milky-Way band across the diagonal, cloudy, a fifth tinted by
//     the cover;
//   - a few distant constellations: 5-7 faint stars joined by barely visible
//     lines, placed out of the cover's zone, redrawn on every track.
// LINEAR values, set against the rendered output (ACES x 1.1 + sRGB lifts
// the bottom of the range steeply): see the measurements in the commit that
// added them.
const FAR_SKY_FLOOR = [0.0022, 0.0027, 0.0055];   // the black is a deep blue night, not zero
const FAR_STAR_CELLS = 250;          // cells over the frame height
const FAR_STAR_FILL = 0.16;          // share of cells lit, outside the band
const FAR_STAR_FILL_BAND = 0.34;     // inside it
const FAR_STAR_BRIGHT = 0.045;
const FAR_BAND_WIDTH = 0.20;         // gaussian half-width, in half-heights
const FAR_BAND_ANGLE = -0.42;        // radians, the band's tilt
const FAR_BAND_BRIGHT = 0.011;
const FAR_BAND_TINT = 0.20;          // share of the cover's colour in the band
const FAR_CONSTELLATIONS = [2, 3, 4];          // by tier
const FAR_CONST_STARS = [5, 7];
const FAR_CONST_STEP = [0.10, 0.22];            // of the half-height, between two stars
const FAR_CONST_STAR_BRIGHT = [0.38, 0.62];
const FAR_CONST_STAR_PX = [2.0, 3.0];
const FAR_CONST_LINE = 0.028;                   // the lines, barely there

// --- drift ------------------------------------------------------------------
// One revolution in eight minutes, about a centre below the frame. Slow enough
// that you cannot catch it moving, present enough that the field is never twice
// the same.
const DRIFT_PERIOD_S = 480;
const DRIFT_PIVOT_Y_FRAC = -1.6;   // of the half-height, i.e. off the bottom

const PEAK_LUMINANCE = 0.92;
const FALLBACK_BIRTH_MIN_MS = 400;
const FALLBACK_BIRTH_MAX_MS = 1400;

const TIER_LAYOUT = {
  0: { stars: 60, lines: 100, background: 150, spikes: false },
  1: { stars: 90, lines: 160, background: 250, spikes: true },
  2: { stars: 120, lines: 220, background: 400, spikes: true }
};
const DEFAULT_TIER = 1;

const FALLBACK_VIVID = [0x8fb8ff, 0xffd9a0, 0xa8ffe4, 0xffa8d8, 0xd8c0ff];
const PALETTE_SIZE = 5;

function urlFlag(name) {
  if (typeof location === 'undefined') return false;
  return new URLSearchParams(location.search).get(name) === '1';
}

function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }

// Own local copy of the safe-zone chunk with its own floor - the shared one in
// safeZone.glsl.js bakes in 0.32. Same "own chunk, own floor" pattern sumi.js
// (0.40) and resonance.js (0.30) already use.
const SAFE_ZONE_FLOOR = 0.30;
const safeZoneChunk = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_ZONE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// The whole field turns as one, so stars and lines share this verbatim and can
// never come apart. Star positions are stored UNDRIFTED and a line stores its
// two endpoints in that same undrifted space - which is why a line needs no
// per-frame update to keep following its stars.
const driftChunk = `
  uniform float uDriftAngle;
  uniform vec2 uDriftPivot;

  vec2 applyDrift(vec2 p) {
    float c = cos(uDriftAngle), s = sin(uDriftAngle);
    vec2 d = p - uDriftPivot;
    return uDriftPivot + vec2(d.x * c - d.y * s, d.x * s + d.y * c);
  }
`;

// Birth, life and the two fades, shared by the star and line shaders so a line
// can never outlive - or out-fade - the star it hangs from.
const lifeChunk = `
  uniform float uTime;

  float lifeFade(float born, float life) {
    float age = uTime - born;
    if (age < 0.0 || age > life) return 0.0;
    float rise = smoothstep(0.0, ${FADE_IN_S.toFixed(2)}, age);
    float fall = 1.0 - smoothstep(life - ${FADE_OUT_S.toFixed(2)}, life, age);
    return rise * fall;
  }
`;

const starVertexShader = `
  precision mediump float;

  attribute float aBorn;
  attribute float aLife;
  attribute float aHaloPx;
  attribute float aBright;
  attribute float aSeed;
  attribute float aSpike;
  attribute vec3 aColor;

  uniform float uPixelScale;
  uniform float uPointSizeMax;
  uniform float uSpikes;

  varying vec3 vColor;
  varying float vBright;
  varying float vHaloPx;
  varying float vPointPx;
  varying float vSpike;
  varying float vSafeAtt;

  ${safeZoneChunk}
  ${driftChunk}
  ${lifeChunk}

  void main() {
    float fade = lifeFade(aBorn, aLife);

    // Slow, independent per star: the seed offsets both the phase and the rate,
    // so no two stars ever pulse together and the field never breathes as one.
    float rate = mix(${TWINKLE_RATE_MIN.toFixed(2)}, ${TWINKLE_RATE_MAX.toFixed(2)}, fract(aSeed * 0.618));
    float twinkle = 1.0 + ${TWINKLE_AMOUNT.toFixed(2)} * sin(uTime * 6.2831853 * rate + aSeed * 12.9898);

    vec3 world = vec3(applyDrift(position.xy), position.z);
    vec4 mv = modelViewMatrix * vec4(world, 1.0);

    // A spiked star needs a bigger sprite than its halo, or the spikes have
    // nowhere to reach; the fragment shader is told the halo size separately so
    // the core and halo keep their real pixel dimensions inside the larger quad.
    float wantSpike = (uSpikes > 0.5 && aSpike > 0.5) ? 1.0 : 0.0;
    float quadPx = aHaloPx * mix(1.0, ${SPIKE_EXTENT.toFixed(2)}, wantSpike);

    vPointPx = clamp(quadPx * fade, 0.0, uPointSizeMax);
    gl_PointSize = max(vPointPx, 0.0);
    vHaloPx = aHaloPx;
    vColor = aColor;
    vBright = aBright * fade * twinkle;
    vSpike = wantSpike;

    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;

const starFragmentShader = `
  precision mediump float;

  varying vec3 vColor;
  varying float vBright;
  varying float vHaloPx;
  varying float vPointPx;
  varying float vSpike;
  varying float vSafeAtt;

  void main() {
    if (vBright <= 0.001 || vPointPx <= 0.0) discard;

    // Work in PIXELS from the sprite's centre, not in normalised sprite space -
    // the sprite is bigger than the halo whenever the star carries spikes, so
    // normalised units would silently rescale the core and halo with it.
    vec2 px = (gl_PointCoord - vec2(0.5)) * vPointPx;
    float r = length(px);

    float halo = 1.0 - smoothstep(0.0, vHaloPx * 0.5, r);
    halo *= halo;
    float core = 1.0 - smoothstep(0.0, ${(CORE_PX * 0.5).toFixed(2)}, r);

    // Four fine spikes: bright along each axis, falling off fast across it and
    // slowly along it. Kept thin on purpose - a fat spike reads as a cross, not
    // as diffraction.
    float spikes = 0.0;
    if (vSpike > 0.5) {
      float reach = vHaloPx * ${(SPIKE_EXTENT * 0.5).toFixed(2)};
      float ax = (1.0 - smoothstep(0.0, 0.9, abs(px.y))) * (1.0 - smoothstep(0.0, reach, abs(px.x)));
      float ay = (1.0 - smoothstep(0.0, 0.9, abs(px.x))) * (1.0 - smoothstep(0.0, reach, abs(px.y)));
      spikes = (ax + ay) * ${SPIKE_STRENGTH.toFixed(2)};
    }

    // The core goes nearly white while the halo keeps the star's hue - that
    // contrast is most of what reads as heat.
    vec3 col = vColor * halo + mix(vColor, vec3(1.0), 0.75) * core + vColor * spikes;
    gl_FragColor = vec4(col * vBright * vSafeAtt * ${PEAK_LUMINANCE.toFixed(2)}, 1.0);
  }
`;

const bgStarVertexShader = `
  precision mediump float;

  attribute float aSizePx;
  attribute float aBright;

  uniform float uPixelScale;
  uniform float uPointSizeMax;

  varying float vBright;
  varying float vSafeAtt;

  ${safeZoneChunk}

  void main() {
    // NOT drifted, deliberately - "400 etoiles de fond minuscules, FIXES". The
    // living field turns; this layer does not, which reads as depth (the far
    // sky behind the near one) and, more practically, is the only way the
    // backdrop stays populated: anything that rotates with the field sweeps out
    // of frame within a couple of minutes and never comes back.
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = clip;
    gl_PointSize = clamp(aSizePx, 1.0, uPointSizeMax);
    vBright = aBright;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;

const bgStarFragmentShader = `
  precision mediump float;
  varying float vBright;
  varying float vSafeAtt;
  void main() {
    vec2 d = gl_PointCoord - vec2(0.5);
    float a = 1.0 - smoothstep(0.0, 0.5, length(d));
    gl_FragColor = vec4(vec3(0.78, 0.84, 1.0) * a * vBright * vSafeAtt, 1.0);
  }
`;

const lineVertexShader = `
  precision mediump float;

  attribute vec2 aFrom;      // the OLDER star - the line grows out of this end
  attribute vec2 aTo;        // the NEW star
  attribute vec3 aColor;
  attribute vec4 aTiming;    // drawStart, drawDur, eraseStart, eraseDur
  attribute float aEraseFromTo; // 1 = the 'to' end is the one that dies

  uniform float uPixelScale;

  varying float vBright;
  varying float vAcross;
  varying float vSafeAtt;
  varying vec3 vColor;

  ${safeZoneChunk}
  ${driftChunk}
  ${lifeChunk}

  void main() {
    float drawStart = aTiming.x, drawDur = aTiming.y;
    float eraseStart = aTiming.z, eraseDur = aTiming.w;

    // The DRAW front: the line reaches from the old star toward the new one. It
    // never appears whole.
    float draw = drawDur > 0.0 ? clamp((uTime - drawStart) / drawDur, 0.0, 1.0) : 0.0;
    // The ERASE front, retreating from whichever end is dying. Which end that is
    // was decided at creation - it is the star with the earlier death - and it
    // is the whole reason a figure coming apart reads as something ending
    // rather than as flicker.
    float erase = eraseDur > 0.0 ? clamp((uTime - eraseStart) / eraseDur, 0.0, 1.0) : 0.0;

    // Both fronts live on the same 0..1 parameter running from 'from' to 'to'.
    float lo = aEraseFromTo > 0.5 ? 0.0 : erase;
    float hi = aEraseFromTo > 0.5 ? (1.0 - erase) : 1.0;
    hi = min(hi, draw);

    float t = mix(lo, hi, position.y);
    vec2 p = applyDrift(mix(aFrom, aTo, t));
    vec2 a = applyDrift(aFrom);
    vec2 b = applyDrift(aTo);

    vec2 dir = b - a;
    float len = length(dir);
    vec2 perp = len > 1e-5 ? vec2(-dir.y, dir.x) / len : vec2(1.0, 0.0);

    // A fully erased or not-yet-started line collapses to nothing: degenerate
    // triangles, no fragments, no branch, and dead slots cost the same.
    float alive = (hi > lo && draw > 0.0) ? 1.0 : 0.0;

    vec3 centre = vec3(p, 0.0);
    vec4 mv = modelViewMatrix * vec4(centre, 1.0);
    float worldPerPx = max(-mv.z, 0.001) / uPixelScale;
    vec3 world = centre + vec3(perp * (position.x * ${(LINE_WIDTH_PX * 0.5).toFixed(2)} * worldPerPx * alive), 0.0);

    vColor = aColor;
    // Softened at both moving fronts, so neither the drawing tip nor the
    // retreating tail is a hard-cut end.
    float nearDraw = 1.0 - smoothstep(0.85, 1.0, t / max(draw, 0.001));
    vBright = alive * ${LINE_BRIGHTNESS.toFixed(2)} * mix(0.55, 1.0, nearDraw);
    vAcross = position.x;

    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    gl_Position = clip;
    vSafeAtt = safeZoneAttenuation(clip);
  }
`;

const lineFragmentShader = `
  precision mediump float;
  varying float vBright;
  varying float vAcross;
  varying float vSafeAtt;
  varying vec3 vColor;
  void main() {
    float across = 1.0 - smoothstep(0.35, 1.0, abs(vAcross));
    gl_FragColor = vec4(vColor * across * vBright * vSafeAtt * ${PEAK_LUMINANCE.toFixed(2)}, 1.0);
  }
`;

const backdropVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const backdropFragmentShader = `
  precision mediump float;

  uniform vec3 uTint;
  uniform float uAspect;
  uniform float uTime;
  uniform float uHalo;
  uniform float uFar;

  varying vec2 vUv;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x), mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float a = 0.5, v = 0.0;
    for (int i = 0; i < 4; i++) { v += a * vnoise(p); p = p * 2.03 + 7.1; a *= 0.5; }
    return v;
  }

  // THE FAR SKY - see FAR_SKY_FLOOR. q is the frame in half-heights.
  vec3 farSky(vec2 q) {
    vec3 col = vec3(${FAR_SKY_FLOOR.map((v) => v.toFixed(4)).join(', ')});
    // The band: across the diagonal, cloudy, a fifth of the cover's colour.
    float c = cos(${FAR_BAND_ANGLE.toFixed(3)}), s = sin(${FAR_BAND_ANGLE.toFixed(3)});
    vec2 r = vec2(c * q.x - s * q.y, s * q.x + c * q.y);
    float band = exp(-pow(r.y / ${FAR_BAND_WIDTH.toFixed(3)}, 2.0));
    float clouds = fbm(r * vec2(2.2, 5.0) + 3.0);
    float lanes = smoothstep(0.35, 0.75, fbm(r * vec2(4.0, 11.0) - 5.0));   // the dark lanes of dust
    vec3 bandCol = mix(vec3(0.62, 0.68, 0.95), uTint * 2.0, ${FAR_BAND_TINT.toFixed(2)});
    col += bandCol * band * (0.35 + 0.9 * clouds) * (1.0 - 0.55 * lanes) * ${FAR_BAND_BRIGHT.toFixed(4)};
    // The sprinkle: one sub-pixel star in some cells, denser in the band.
    vec2 g = q * ${(FAR_STAR_CELLS / 2).toFixed(1)};
    vec2 cell = floor(g), f = fract(g) - 0.5;
    float h = hash12(cell);
    float fill = mix(${FAR_STAR_FILL.toFixed(2)}, ${FAR_STAR_FILL_BAND.toFixed(2)}, band);
    vec2 jitter = (vec2(hash12(cell + 11.0), hash12(cell + 23.0)) - 0.5) * 0.5;
    float point = 1.0 - smoothstep(0.08, 0.34, length(f - jitter));
    float b = hash12(cell + 37.0);
    float tw = 0.8 + 0.2 * sin(uTime * (0.4 + 1.2 * b) + h * 40.0);
    col += vec3(0.82, 0.86, 1.0) * step(h, fill) * point * b * b * tw * ${FAR_STAR_BRIGHT.toFixed(3)};
    return col;
  }

  void main() {
    // Deep black with the faintest wash toward a colour taken from the cover -
    // enough that the frame is not a dead rectangle, never enough to read as a
    // coloured background.
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float d = clamp(1.0 - length(p) / ${BG_HALO_RADIUS.toFixed(3)}, 0.0, 1.0);
    float radial = pow(d, ${BG_HALO_FALLOFF.toFixed(1)});
    vec3 col = uTint * radial * ${BG_TINT_STRENGTH.toFixed(3)} * uHalo;
    col += farSky(p * 2.0) * uFar;

    // Fine grain, static per pixel rather than crawling - a crawling grain on a
    // still night sky reads as video noise. Purely spatial and purely additive:
    // it is NOT modulated by the gradient, so it cannot trace the gradient's own
    // contours the way luminance-scaled noise does.
    float g = hash12(floor(vUv * 1400.0));
    col += vec3((g - 0.5) * ${BG_GRAIN.toFixed(3)});

    // ORDERED DITHER, immediately before the value is quantised to 8 bits, and
    // the actual fix for the rings. A gradient this shallow holds each output
    // level for tens of pixels, and a plateau tens of pixels wide IS a visible
    // ring - no amount of grain on top hides it, grain only lands on the
    // plateaus and draws attention to them. Half a level of noise at exactly
    // the quantisation step scatters the boundary between two levels into a
    // dithered mix instead of a hard edge. It is under the eye's threshold and
    // it removes the banding at its source rather than masking it.
    float dither = (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
    col += dither;

    gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
  }
`;

export class ConstellationVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._maxStars = layout.stars;
    this._maxLines = layout.lines;
    this._spikes = layout.spikes;

    this._halfHeight = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * CAMERA_DIST;
    this._halfWidth = this._halfHeight * camera.aspect;
    this._linkMaxDist = this._halfHeight * LINK_MAX_DIST_FRAC;

    this._time = 0;
    this._lastBirth = -1e6;
    this._nextFallbackBirth = 0;
    this._prevBands = [0, 0, 0];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._palette = FALLBACK_VIVID.map((c) => new THREE.Color(c));
    this._sizeSpread = 1;
    this._lifeScale = 0.5;
    // Drift state is read by _insideSafeZone, which a birth can reach on the
    // very first frame - so it is initialised here, not first assigned in
    // update().
    this._driftAngle = 0;
    this._driftPivotY = this._halfHeight * DRIFT_PIVOT_Y_FRAC;
    // Every scratch object this file will ever need, allocated once. update()
    // and the birth path below must not allocate.
    this._scratchXY = new Float32Array(2);
    this._scratchBase = new Float32Array(2);
    this._scratchColor = new THREE.Color();
    this._scratchHsl = { h: 0, s: 0, l: 0 };

    this._buildBackdrop();
    this._buildStars();
    this._buildLines();
    this._buildBackgroundStars(layout.background);
    this._buildFarConstellations(FAR_CONSTELLATIONS[this._tier] != null ? FAR_CONSTELLATIONS[this._tier] : 3);
    this._seedFarConstellations();
    this._buildGrid();

    // The three uniform sets that share a clock, a pixel scale and the drift.
    // Held as a list so update() writes them in one loop and cannot forget one.
    this._uniformSets = [this._starUniforms, this._lineUniforms, this._bgUniforms, this._farUniforms];
  }

  // --- geometry -------------------------------------------------------------

  _buildBackdrop() {
    this._backdropGeometry = new THREE.PlaneGeometry(1, 1);
    this._backdropUniforms = {
      uTint: { value: new THREE.Color(0x2a3a6e) },
      uAspect: { value: this._camera.aspect },
      uTime: { value: 0 },
      // ?nohalo=1 - drop the wash entirely, to check it is actually earning its
      // place rather than just being tolerated.
      uHalo: { value: urlFlag('nohalo') ? 0 : 1 },
      // ?nofar=1 - the far sky off, to compare.
      uFar: { value: urlFlag('nofar') ? 0 : 1 }
    };
    this._backdropMaterial = new THREE.ShaderMaterial({
      uniforms: this._backdropUniforms,
      vertexShader: backdropVertexShader,
      fragmentShader: backdropFragmentShader,
      depthTest: false,
      depthWrite: false,
      transparent: false
    });
    this._backdrop = new THREE.Mesh(this._backdropGeometry, this._backdropMaterial);
    this._backdrop.frustumCulled = false;
    this._backdrop.renderOrder = -100;
    this._scene.add(this._backdrop);
  }

  _sharedUniforms() {
    return Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uPixelScale: { value: 1 },
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uDriftAngle: { value: 0 },
      uDriftPivot: { value: new THREE.Vector2(0, this._halfHeight * DRIFT_PIVOT_Y_FRAC) }
    });
  }

  // The ring buffer. Every array here is allocated exactly once; a birth
  // overwrites one slot and uploads that slot alone.
  _buildStars() {
    const n = this._maxStars;
    this._starPos = new Float32Array(n * 3);
    this._starColor = new Float32Array(n * 3);
    this._starBorn = new Float32Array(n);
    this._starLife = new Float32Array(n);
    this._starHalo = new Float32Array(n);
    this._starBright = new Float32Array(n);
    this._starSeed = new Float32Array(n);
    this._starSpike = new Float32Array(n);
    // Born far enough in the past that lifeFade() returns 0 for every slot
    // before anything has actually been born into it.
    for (let i = 0; i < n; i++) { this._starBorn[i] = -1e6; this._starLife[i] = 0.001; }
    this._starWrite = 0;
    this._starCount = 0;

    const g = new THREE.BufferGeometry();
    this._starPosAttr = new THREE.BufferAttribute(this._starPos, 3);
    this._starColorAttr = new THREE.BufferAttribute(this._starColor, 3);
    this._starBornAttr = new THREE.BufferAttribute(this._starBorn, 1);
    this._starLifeAttr = new THREE.BufferAttribute(this._starLife, 1);
    this._starHaloAttr = new THREE.BufferAttribute(this._starHalo, 1);
    this._starBrightAttr = new THREE.BufferAttribute(this._starBright, 1);
    this._starSeedAttr = new THREE.BufferAttribute(this._starSeed, 1);
    this._starSpikeAttr = new THREE.BufferAttribute(this._starSpike, 1);
    g.setAttribute('position', this._starPosAttr);
    g.setAttribute('aColor', this._starColorAttr);
    g.setAttribute('aBorn', this._starBornAttr);
    g.setAttribute('aLife', this._starLifeAttr);
    g.setAttribute('aHaloPx', this._starHaloAttr);
    g.setAttribute('aBright', this._starBrightAttr);
    g.setAttribute('aSeed', this._starSeedAttr);
    g.setAttribute('aSpike', this._starSpikeAttr);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);

    this._starUniforms = Object.assign(this._sharedUniforms(), {
      uSpikes: { value: this._spikes ? 1 : 0 }
    });
    this._starMaterial = new THREE.ShaderMaterial({
      uniforms: this._starUniforms,
      vertexShader: starVertexShader,
      fragmentShader: starFragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._starGeometry = g;
    this._starPoints = new THREE.Points(g, this._starMaterial);
    this._starPoints.frustumCulled = false;
    this._starPoints.renderOrder = 2;
    this._scene.add(this._starPoints);
  }

  _buildLines() {
    const n = this._maxLines;
    this._lineFrom = new Float32Array(n * 2);
    this._lineTo = new Float32Array(n * 2);
    this._lineColor = new Float32Array(n * 3);
    this._lineTiming = new Float32Array(n * 4);
    this._lineEraseFromTo = new Float32Array(n);
    // Wall-clock end of each line, CPU-side only, for slot reuse and the live
    // cap. Never uploaded.
    this._lineEnd = new Float32Array(n);
    for (let i = 0; i < n; i++) this._lineEnd[i] = -1e6;
    this._lineWrite = 0;

    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const g = new THREE.InstancedBufferGeometry();
    g.setIndex([0, 1, 2, 0, 2, 3]);
    g.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    this._lineFromAttr = new THREE.InstancedBufferAttribute(this._lineFrom, 2);
    this._lineToAttr = new THREE.InstancedBufferAttribute(this._lineTo, 2);
    this._lineColorAttr = new THREE.InstancedBufferAttribute(this._lineColor, 3);
    this._lineTimingAttr = new THREE.InstancedBufferAttribute(this._lineTiming, 4);
    this._lineEraseAttr = new THREE.InstancedBufferAttribute(this._lineEraseFromTo, 1);
    g.setAttribute('aFrom', this._lineFromAttr);
    g.setAttribute('aTo', this._lineToAttr);
    g.setAttribute('aColor', this._lineColorAttr);
    g.setAttribute('aTiming', this._lineTimingAttr);
    g.setAttribute('aEraseFromTo', this._lineEraseAttr);
    g.instanceCount = n;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);

    this._lineUniforms = this._sharedUniforms();
    this._lineMaterial = new THREE.ShaderMaterial({
      uniforms: this._lineUniforms,
      vertexShader: lineVertexShader,
      fragmentShader: lineFragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._lineGeometry = g;
    this._lineMesh = new THREE.Mesh(g, this._lineMaterial);
    this._lineMesh.frustumCulled = false;
    this._lineMesh.renderOrder = 1;
    this._scene.add(this._lineMesh);
  }

  _buildBackgroundStars(count) {
    this._bgCount = count;
    this._bgPos = new Float32Array(count * 3);
    this._bgSize = new Float32Array(count);
    this._bgBright = new Float32Array(count);

    const g = new THREE.BufferGeometry();
    this._bgPosAttr = new THREE.BufferAttribute(this._bgPos, 3);
    this._bgSizeAttr = new THREE.BufferAttribute(this._bgSize, 1);
    this._bgBrightAttr = new THREE.BufferAttribute(this._bgBright, 1);
    g.setAttribute('position', this._bgPosAttr);
    g.setAttribute('aSizePx', this._bgSizeAttr);
    g.setAttribute('aBright', this._bgBrightAttr);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);

    this._bgUniforms = this._sharedUniforms();
    this._bgMaterial = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: bgStarVertexShader,
      fragmentShader: bgStarFragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._bgGeometry = g;
    this._bgPoints = new THREE.Points(g, this._bgMaterial);
    this._bgPoints.frustumCulled = false;
    this._bgPoints.renderOrder = 0;
    this._scene.add(this._bgPoints);
    this._seedBackground();
  }

  // The distant constellations: points on the background stars' own shader,
  // lines on a thin additive shader of their own. Fixed like the background.
  _buildFarConstellations(count) {
    const maxStars = count * FAR_CONST_STARS[1];
    this._farCount = count;
    this._farPos = new Float32Array(maxStars * 3);
    this._farSize = new Float32Array(maxStars);
    this._farBright = new Float32Array(maxStars);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this._farPos, 3));
    g.setAttribute('aSizePx', new THREE.BufferAttribute(this._farSize, 1));
    g.setAttribute('aBright', new THREE.BufferAttribute(this._farBright, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);
    this._farUniforms = this._sharedUniforms();
    this._farMaterial = new THREE.ShaderMaterial({
      uniforms: this._farUniforms, vertexShader: bgStarVertexShader, fragmentShader: bgStarFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._farGeometry = g;
    this._farPoints = new THREE.Points(g, this._farMaterial);
    this._farPoints.frustumCulled = false;
    this._farPoints.renderOrder = -1;
    this._scene.add(this._farPoints);

    const lg = new THREE.BufferGeometry();
    this._farLinePos = new Float32Array(maxStars * 2 * 3);
    lg.setAttribute('position', new THREE.BufferAttribute(this._farLinePos, 3));
    lg.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);
    this._farLineMaterial = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, safeZoneUniforms, { uBright: { value: FAR_CONST_LINE } }),
      vertexShader: `
        precision mediump float;
        varying float vSafeAtt;
        ${safeZoneChunk}
        void main() { vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position = clip; vSafeAtt = safeZoneAttenuation(clip); }`,
      fragmentShader: `
        precision mediump float;
        uniform float uBright;
        varying float vSafeAtt;
        void main() { gl_FragColor = vec4(vec3(0.72, 0.78, 1.0) * uBright * vSafeAtt, 1.0); }`,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._farLineGeometry = lg;
    this._farLines = new THREE.LineSegments(lg, this._farLineMaterial);
    this._farLines.frustumCulled = false;
    this._farLines.renderOrder = -2;
    this._scene.add(this._farLines);
  }

  // A constellation is a chain that wanders - each star a step from the last,
  // the heading turning a little - with one branch off its middle, like the
  // figures people actually drew. Kept out of the cover's zone and apart from
  // each other; redrawn on every track, with the background.
  _seedFarConstellations() {
    if (!this._farPos) return;
    const hw = this._halfWidth * 0.95, hh = this._halfHeight * 0.92;
    const centres = [];
    let n = 0, l = 0;
    for (let k = 0; k < this._farCount; k++) {
      let cx = 0, cy = 0;
      for (let tries = 0; tries < 30; tries++) {
        cx = (Math.random() * 2 - 1) * hw * 0.8; cy = (Math.random() * 2 - 1) * hh * 0.8;
        const clearCover = !this._insideSafeZone || !this._insideSafeZone(cx, cy);
        const apart = centres.every((c) => Math.hypot(c[0] - cx, c[1] - cy) > this._halfHeight * 0.7);
        if (clearCover && apart) break;
      }
      centres.push([cx, cy]);
      const count = FAR_CONST_STARS[0] + Math.floor(Math.random() * (FAR_CONST_STARS[1] - FAR_CONST_STARS[0] + 1));
      let x = cx, y = cy, heading = Math.random() * Math.PI * 2;
      const first = n;
      for (let i = 0; i < count; i++) {
        if (i > 0) {
          heading += (Math.random() - 0.5) * 1.6;
          const step = lerp(FAR_CONST_STEP[0], FAR_CONST_STEP[1], Math.random()) * this._halfHeight;
          // Kept in the frame: a step that would leave it turns back instead.
          let nx = x + Math.cos(heading) * step, ny = y + Math.sin(heading) * step;
          if (Math.abs(nx) > hw || Math.abs(ny) > hh) { heading += Math.PI; nx = x + Math.cos(heading) * step; ny = y + Math.sin(heading) * step; }
          x = Math.max(-hw, Math.min(hw, nx)); y = Math.max(-hh, Math.min(hh, ny));
        }
        this._farPos[n * 3] = x; this._farPos[n * 3 + 1] = y; this._farPos[n * 3 + 2] = 0;
        this._farSize[n] = lerp(FAR_CONST_STAR_PX[0], FAR_CONST_STAR_PX[1], Math.random());
        this._farBright[n] = lerp(FAR_CONST_STAR_BRIGHT[0], FAR_CONST_STAR_BRIGHT[1], Math.random());
        // the chain, and a branch from the middle star to the last one
        const from = i === count - 1 && count >= 5 ? first + Math.floor(count / 2) : n - 1;
        if (i > 0 && !(i === count - 1 && count >= 5)) {
          this._farLinePos.set([this._farPos[(n - 1) * 3], this._farPos[(n - 1) * 3 + 1], 0, x, y, 0], l * 6); l++;
        } else if (i === count - 1 && count >= 5) {
          this._farLinePos.set([this._farPos[from * 3], this._farPos[from * 3 + 1], 0, x, y, 0], l * 6); l++;
        }
        n++;
      }
    }
    this._farGeometry.setDrawRange(0, n);
    this._farGeometry.attributes.position.needsUpdate = true;
    this._farGeometry.attributes.aSizePx.needsUpdate = true;
    this._farGeometry.attributes.aBright.needsUpdate = true;
    this._farLineGeometry.setDrawRange(0, l * 2);
    this._farLineGeometry.attributes.position.needsUpdate = true;
  }

  // Refilled, never reallocated - so "generated once per track" costs nothing
  // beyond one upload.
  _seedBackground() {
    const w = this._halfWidth * 1.5, h = this._halfHeight * 1.5;
    for (let i = 0; i < this._bgCount; i++) {
      this._bgPos[i * 3 + 0] = (Math.random() * 2 - 1) * w;
      this._bgPos[i * 3 + 1] = (Math.random() * 2 - 1) * h;
      this._bgPos[i * 3 + 2] = 0;
      const t = Math.random();
      this._bgSize[i] = lerp(BG_STAR_PX_MIN, BG_STAR_PX_MAX, t * t);
      this._bgBright[i] = lerp(BG_STAR_BRIGHT_MIN, BG_STAR_BRIGHT_MAX, Math.random());
    }
    this._bgPosAttr.needsUpdate = true;
    this._bgSizeAttr.needsUpdate = true;
    this._bgBrightAttr.needsUpdate = true;
  }

  // --- spatial grid ---------------------------------------------------------
  // Criterion 6. A linked list per cell, in two flat Int32Arrays: head[] holds
  // the first star in each cell, next[] the following one. O(1) insert, and a
  // removal only walks its own cell, which holds a handful of entries.
  //
  // Without it a birth compares against every living star; at 120 stars and a
  // birth every 120 ms that is the 14 000 comparisons a second the brief warns
  // about, for a query whose answer only ever involves the two or three stars
  // physically nearby.
  _buildGrid() {
    this._cell = this._linkMaxDist;
    // The grid indexes UNDRIFTED positions, and those are spread around the
    // whole circle the field turns through - a star born on screen late in a
    // revolution has an undrifted position way outside the frame. So the grid
    // is centred on the drift pivot and sized to the swept disc; sizing it to
    // the frame instead would clamp most stars into the edge cells and the
    // neighbour search would start returning stars that are nowhere near.
    const pivotY = this._halfHeight * DRIFT_PIVOT_Y_FRAC;
    this._gridR = Math.hypot(this._halfWidth, this._halfHeight - pivotY) * 1.1;
    this._gridCx = 0;
    this._gridCy = pivotY;
    this._gridW = this._gridR * 2;
    this._gridH = this._gridR * 2;
    this._cols = Math.max(1, Math.ceil(this._gridW / this._cell));
    this._rows = Math.max(1, Math.ceil(this._gridH / this._cell));
    this._gridHead = new Int32Array(this._cols * this._rows).fill(-1);
    this._gridNext = new Int32Array(this._maxStars).fill(-1);
    this._starCell = new Int32Array(this._maxStars).fill(-1);
    // Scratch for neighbour queries - allocated once, reused every birth.
    this._nbIndex = new Int32Array(LINK_NEIGHBOURS_MAX);
    this._nbDist = new Float32Array(LINK_NEIGHBOURS_MAX);
  }

  _colOf(x) {
    return clamp(Math.floor((x - this._gridCx + this._gridR) / this._cell), 0, this._cols - 1);
  }

  _rowOf(y) {
    return clamp(Math.floor((y - this._gridCy + this._gridR) / this._cell), 0, this._rows - 1);
  }

  _cellOf(x, y) {
    return this._rowOf(y) * this._cols + this._colOf(x);
  }

  _gridInsert(i, x, y) {
    const c = this._cellOf(x, y);
    this._starCell[i] = c;
    this._gridNext[i] = this._gridHead[c];
    this._gridHead[c] = i;
  }

  _gridRemove(i) {
    const c = this._starCell[i];
    if (c < 0) return;
    let cur = this._gridHead[c], prev = -1;
    while (cur !== -1) {
      if (cur === i) {
        if (prev === -1) this._gridHead[c] = this._gridNext[i];
        else this._gridNext[prev] = this._gridNext[i];
        break;
      }
      prev = cur;
      cur = this._gridNext[cur];
    }
    this._starCell[i] = -1;
    this._gridNext[i] = -1;
  }

  // Distance to the nearest LIVING star, searching only the 3x3 cell
  // neighbourhood. Returns Infinity when the neighbourhood is empty.
  _nearestDistance(x, y, ignore) {
    const cx = this._colOf(x);
    const cy = this._rowOf(y);
    let best = Infinity;
    for (let gy = cy - 1; gy <= cy + 1; gy++) {
      if (gy < 0 || gy >= this._rows) continue;
      for (let gx = cx - 1; gx <= cx + 1; gx++) {
        if (gx < 0 || gx >= this._cols) continue;
        let i = this._gridHead[gy * this._cols + gx];
        while (i !== -1) {
          if (i !== ignore && this._isAlive(i)) {
            const dx = this._starPos[i * 3] - x, dy = this._starPos[i * 3 + 1] - y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < best) best = d;
          }
          i = this._gridNext[i];
        }
      }
    }
    return best;
  }

  _isAlive(i) {
    const age = this._time - this._starBorn[i];
    return age >= 0 && age < this._starLife[i];
  }

  // --- births ---------------------------------------------------------------

  _minSpacingS(profile) {
    const character = profile.director && profile.director.character;
    const density = character && character.density != null ? character.density : 0.5;
    return lerp(BIRTH_SPACING_SPARSE_MS, BIRTH_SPACING_DENSE_MS, clamp01(density)) / 1000;
  }

  // Where the star goes. Candidates are drawn inside the band's own third of
  // the frame, rejected outright if they fall behind the cover (criterion 5),
  // and otherwise scored by how far they sit from the nearest living star -
  // the "soft repulsion". Best of PLACEMENT_CANDIDATES, with an early exit as
  // soon as one is comfortably clear, so a sparse sky costs one sample.
  _placeStar(band, out) {
    const range = BAND_Y_RANGE[band];
    let bestX = 0, bestY = 0, bestScore = -1, found = false;
    for (let k = 0; k < PLACEMENT_CANDIDATES; k++) {
      // Candidates are drawn where the star will actually BE - on screen, in
      // its band's own third - and then rotated backwards into the undrifted
      // space everything is stored in. Sampling directly in that stored space
      // is what an earlier version did, and it put stars wherever the drift
      // happened to be pointing: measured, 118 of 118 living stars had been
      // deposited outside the frame and the visible sky was empty.
      const sx = (Math.random() * 2 - 1) * this._halfWidth * 0.96;
      const sy = lerp(range[0], range[1], Math.random()) * this._halfHeight * 0.96;
      if (this._insideSafeZone(sx, sy)) continue;
      this._undrift(sx, sy, this._scratchBase);
      const x = this._scratchBase[0], y = this._scratchBase[1];
      const score = this._nearestDistance(x, y, -1);
      if (score > bestScore) { bestScore = score; bestX = x; bestY = y; found = true; }
      if (score >= PLACEMENT_GOOD_ENOUGH) break;
    }
    if (!found) return false;
    out[0] = bestX; out[1] = bestY;
    return true;
  }

  // Screen -> stored space: the inverse of the shaders' applyDrift().
  _undrift(sx, sy, out) {
    const a = -this._driftAngle;
    const px = this._driftPivotY;
    const c = Math.cos(a), s = Math.sin(a);
    const dx = sx, dy = sy - px;
    out[0] = dx * c - dy * s;
    out[1] = px + dx * s + dy * c;
  }

  // Criterion 5, tested on the ON-SCREEN position - which is what _placeStar
  // now samples directly.
  //
  // The safe zone is expressed in "height units": x scaled by the aspect, y in
  // [-1, 1] across the frame. This camera puts the field on the z = 0 plane,
  // where ndc.y = y / halfHeight and ndc.x * aspect = x / halfHeight, so both
  // axes are simply world / halfHeight.
  _insideSafeZone(screenX, screenY) {
    const sx = screenX / this._halfHeight, sy = screenY / this._halfHeight;
    // R19: the exclusion is the stage block, not the cover; no cover (zero
    // size) still means no exclusion, as before.
    if (safeZoneUniforms.uSafeHalfSize.value.x <= 0) return false;
    const cen = safeZoneUniforms.uStageCenter.value;
    const half = safeZoneUniforms.uStageHalfSize.value;
    const m = SAFE_ZONE_BIRTH_MARGIN;
    return Math.abs(sx - cen.x) < half.x + m && Math.abs(sy - cen.y) < half.y + m;
  }

  // A slot may only be taken if whatever was in it has finished dying. A plain
  // ring-buffer write does NOT hold here: the birth rate the brief asks for
  // outruns the buffer (at 300 ms and a 40 s life the sky wants 133 stars in
  // 120 slots), so the cursor laps the living and overwrites them - and an
  // overwritten star does not fade, it blinks out mid-life, taking its lines
  // with it. That is exactly the abruptness the 4-second extinction exists to
  // prevent. When the sky is genuinely full the birth is DROPPED instead; the
  // next rise a moment later finds room.
  _freeStarSlot() {
    for (let k = 0; k < this._maxStars; k++) {
      const c = (this._starWrite + k) % this._maxStars;
      if (!this._isAlive(c)) return c;
    }
    return -1;
  }

  _birthStar(band, profile) {
    const slot = this._freeStarSlot();
    if (slot < 0) return;
    if (!this._placeStar(band, this._scratchXY)) return;

    // Reusing a slot means the old occupant leaves the grid first, or the
    // neighbour search would keep proposing a star that no longer exists.
    this._gridRemove(slot);

    const x = this._scratchXY[0], y = this._scratchXY[1];
    this._starPos[slot * 3] = x;
    this._starPos[slot * 3 + 1] = y;
    this._starPos[slot * 3 + 2] = 0;

    // Size spread widens with character.dynamics: the band sizes are pushed
    // away from (or pulled toward) their own mean, so a flat track gives stars
    // that are all much of a muchness and a dynamic one lets the low-band
    // stars tower over the high-band ones. Scaling the raw size instead would
    // just make the whole sky bigger, which is not the same thing at all.
    const mean = (BAND_HALO_PX[0] + BAND_HALO_PX[2]) / 2;
    this._starHalo[slot] = mean + (BAND_HALO_PX[band] - mean) * this._sizeSpread;
    const bright = BAND_BRIGHT[band] * (0.85 + Math.random() * 0.3);
    this._starBright[slot] = bright;
    this._starSpike[slot] = bright >= SPIKE_BRIGHTNESS_MIN ? 1 : 0;
    this._starSeed[slot] = Math.random() * 100;
    this._starBorn[slot] = this._time;
    this._starLife[slot] = lerp(LIFE_MIN_S, LIFE_MAX_S, this._lifeScale) * (0.85 + Math.random() * 0.3);

    const col = this._palette[band % this._palette.length];
    this._starColor[slot * 3] = col.r;
    this._starColor[slot * 3 + 1] = col.g;
    this._starColor[slot * 3 + 2] = col.b;

    // One partial upload per attribute per birth - never a full re-upload of
    // the sky, and nothing at all on a frame that births nothing.
    this._starPosAttr.addUpdateRange(slot * 3, 3); this._starPosAttr.needsUpdate = true;
    this._starColorAttr.addUpdateRange(slot * 3, 3); this._starColorAttr.needsUpdate = true;
    this._starBornAttr.addUpdateRange(slot, 1); this._starBornAttr.needsUpdate = true;
    this._starLifeAttr.addUpdateRange(slot, 1); this._starLifeAttr.needsUpdate = true;
    this._starHaloAttr.addUpdateRange(slot, 1); this._starHaloAttr.needsUpdate = true;
    this._starBrightAttr.addUpdateRange(slot, 1); this._starBrightAttr.needsUpdate = true;
    this._starSeedAttr.addUpdateRange(slot, 1); this._starSeedAttr.needsUpdate = true;
    this._starSpikeAttr.addUpdateRange(slot, 1); this._starSpikeAttr.needsUpdate = true;

    this._gridInsert(slot, x, y);
    this._starWrite = (slot + 1) % this._maxStars;
    this._starCount = Math.min(this._starCount + 1, this._maxStars);
    this._lastBirth = this._time;

    this._linkNewStar(slot);
  }

  // --- linking --------------------------------------------------------------

  // The new star reaches back to its 2-3 nearest ALREADY LIVING neighbours,
  // found through the grid's 3x3 neighbourhood rather than by scanning the
  // field. A small insertion sort into a 3-slot buffer keeps the best few
  // without sorting the candidate list.
  _linkNewStar(slot) {
    const x = this._starPos[slot * 3], y = this._starPos[slot * 3 + 1];
    let found = 0;
    for (let k = 0; k < LINK_NEIGHBOURS_MAX; k++) { this._nbIndex[k] = -1; this._nbDist[k] = Infinity; }

    const cx = this._colOf(x);
    const cy = this._rowOf(y);
    for (let gy = cy - 1; gy <= cy + 1; gy++) {
      if (gy < 0 || gy >= this._rows) continue;
      for (let gx = cx - 1; gx <= cx + 1; gx++) {
        if (gx < 0 || gx >= this._cols) continue;
        let i = this._gridHead[gy * this._cols + gx];
        while (i !== -1) {
          if (i !== slot && this._isAlive(i)) {
            const dx = this._starPos[i * 3] - x, dy = this._starPos[i * 3 + 1] - y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d <= this._linkMaxDist) {
              for (let k = 0; k < LINK_NEIGHBOURS_MAX; k++) {
                if (d < this._nbDist[k]) {
                  for (let m = LINK_NEIGHBOURS_MAX - 1; m > k; m--) {
                    this._nbDist[m] = this._nbDist[m - 1];
                    this._nbIndex[m] = this._nbIndex[m - 1];
                  }
                  this._nbDist[k] = d; this._nbIndex[k] = i;
                  found = Math.min(found + 1, LINK_NEIGHBOURS_MAX);
                  break;
                }
              }
            }
          }
          i = this._gridNext[i];
        }
      }
    }

    if (found <= 0) return;
    // Two or three, never more - the cap on how fast the graph can densify.
    const want = Math.min(found,
      LINK_NEIGHBOURS_MIN + (Math.random() < 0.5 ? 0 : LINK_NEIGHBOURS_MAX - LINK_NEIGHBOURS_MIN));
    let live = this._countLiveLines();
    for (let k = 0; k < want; k++) {
      const other = this._nbIndex[k];
      if (other < 0) continue;
      // Criterion 3: past the cap the sky simply stops gaining lines. The stars
      // keep arriving, so the field still breathes - it just never becomes a
      // mesh, no matter how long it runs.
      if (live >= this._maxLines) break;
      if (this._addLine(other, slot)) live++;
    }
  }

  _countLiveLines() {
    let n = 0;
    for (let i = 0; i < this._maxLines; i++) if (this._lineEnd[i] > this._time) n++;
    return n;
  }

  // from = the OLDER star (the line grows out of it), to = the new one.
  _addLine(from, to) {
    // Find a slot whose line has finished. The write cursor makes this O(1)
    // amortised rather than a scan from zero every time.
    let slot = -1;
    for (let k = 0; k < this._maxLines; k++) {
      const c = (this._lineWrite + k) % this._maxLines;
      if (this._lineEnd[c] <= this._time) { slot = c; break; }
    }
    if (slot < 0) return false;
    this._lineWrite = (slot + 1) % this._maxLines;

    const fx = this._starPos[from * 3], fy = this._starPos[from * 3 + 1];
    const tx = this._starPos[to * 3], ty = this._starPos[to * 3 + 1];
    this._lineFrom[slot * 2] = fx; this._lineFrom[slot * 2 + 1] = fy;
    this._lineTo[slot * 2] = tx; this._lineTo[slot * 2 + 1] = ty;

    // The line's colour is the mean of its two endpoints'.
    for (let c = 0; c < 3; c++) {
      this._lineColor[slot * 3 + c] =
        (this._starColor[from * 3 + c] + this._starColor[to * 3 + c]) * 0.5;
    }

    // WHICH END DIES, decided here and never revisited: the star with the
    // earlier death takes the line with it, and the erase front retreats from
    // that end. Criterion 2 is a consequence of resolving this at creation -
    // there is no per-frame decision that could get it wrong.
    const deathFrom = this._starBorn[from] + this._starLife[from];
    const deathTo = this._starBorn[to] + this._starLife[to];
    const death = Math.min(deathFrom, deathTo);
    const eraseFromTo = deathTo <= deathFrom ? 1 : 0;

    const drawDur = lerp(LINE_DRAW_MIN_S, LINE_DRAW_MAX_S, Math.random());
    const eraseDur = Math.min(FADE_OUT_S, Math.max(0.3, death - this._time));
    this._lineTiming[slot * 4 + 0] = this._time;
    this._lineTiming[slot * 4 + 1] = drawDur;
    this._lineTiming[slot * 4 + 2] = death - eraseDur;
    this._lineTiming[slot * 4 + 3] = eraseDur;
    this._lineEraseFromTo[slot] = eraseFromTo;
    this._lineEnd[slot] = death;

    this._lineFromAttr.addUpdateRange(slot * 2, 2); this._lineFromAttr.needsUpdate = true;
    this._lineToAttr.addUpdateRange(slot * 2, 2); this._lineToAttr.needsUpdate = true;
    this._lineColorAttr.addUpdateRange(slot * 3, 3); this._lineColorAttr.needsUpdate = true;
    this._lineTimingAttr.addUpdateRange(slot * 4, 4); this._lineTimingAttr.needsUpdate = true;
    this._lineEraseAttr.addUpdateRange(slot, 1); this._lineEraseAttr.needsUpdate = true;
    return true;
  }

  // --- frame ----------------------------------------------------------------

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors) || [];
    for (let i = 0; i < PALETTE_SIZE; i++) {
      this._palette[i].set(vivid.length ? vivid[i % vivid.length] : FALLBACK_VIVID[i]);
    }
    const dominant = (palette && palette.dominantColors && palette.dominantColors.length)
      ? palette.dominantColors[0] : 0x2a3a6e;
    // A DARK, MOSTLY DESATURATED derivative - not the cover colour itself. The
    // hue survives, four fifths of the saturation does not, and
    // BG_TINT_STRENGTH then keeps the whole thing near black. Boosting the
    // saturation here (an earlier version added 0.15 to it) is what turned a
    // merely greenish cover into a frank green wash.
    const tmp = this._scratchColor.set(dominant);
    const hsl = this._scratchHsl;
    tmp.getHSL(hsl);
    this._backdropUniforms.uTint.value.setHSL(
      hsl.h, hsl.s * BG_TINT_SATURATION, 0.5);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // A total freeze, in-flight line draws included: uTime is the only clock
    // any of the three shaders reads.
    if (!paused) this._time += deltaTime;

    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      if (this._lastTrackId !== undefined) { this._seedBackground(); this._seedFarConstellations(); }
    }

    const palette = profile.artworkPalette;
    if (palette !== this._lastPaletteRef) {
      this._lastPaletteRef = palette;
      this._applyPalette(palette);
    }

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const live = !!(director && director.hasReliableGrid && bands);
    const character = (director && director.character) || null;

    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    this._sizeSpread = lerp(SIZE_SPREAD_MIN, SIZE_SPREAD_MAX, clamp01(dynamics));
    // The sky fills toward the middle of the track and empties again.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._lifeScale = Math.sin(Math.PI * progress);

    this._driftAngle = (this._time / DRIFT_PERIOD_S) * Math.PI * 2;
    this._driftPivotY = this._halfHeight * DRIFT_PIVOT_Y_FRAC;

    if (!paused) {
      if (live) {
        // A star is born on a RISE, never on a level - the same rule sumi.js
        // and resonance.js use. Rise tracking runs every frame, before the
        // spacing gate, so a rise that happens while the gate is shut is a
        // missed birth rather than a stale comparison later.
        const b = [bands.low, bands.mid, bands.high];
        let bestBand = -1, bestRise = RISE_THRESHOLD;
        for (let i = 0; i < 3; i++) {
          const rise = b[i] - this._prevBands[i];
          this._prevBands[i] = b[i];
          if (rise >= bestRise) { bestRise = rise; bestBand = i; }
        }
        if (bestBand >= 0 && this._time - this._lastBirth >= this._minSpacingS(profile)) {
          this._birthStar(bestBand, profile);
        }
      } else if (this._time >= this._nextFallbackBirth) {
        // No analysis: births on their own irregular clock. The piece is about
        // accumulation, and accumulation does not need to know the tempo.
        this._birthStar(Math.floor(Math.random() * 3), profile);
        this._nextFallbackBirth = this._time
          + lerp(FALLBACK_BIRTH_MIN_MS, FALLBACK_BIRTH_MAX_MS, Math.random()) / 1000;
      }
    }

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    const pixelScale = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    // Three uniform sets, written as scalars - no allocation, no upload.
    const sets = this._uniformSets;
    for (let i = 0; i < sets.length; i++) {
      sets[i].uTime.value = this._time;
      sets[i].uPixelScale.value = pixelScale;
      sets[i].uDriftAngle.value = this._driftAngle;
    }
    this._backdropUniforms.uAspect.value = this._camera.aspect;
    this._backdropUniforms.uTime.value = this._time;
  }

  dispose() {
    this._scene.remove(this._starPoints);
    this._scene.remove(this._lineMesh);
    this._scene.remove(this._bgPoints);
    this._scene.remove(this._backdrop);
    this._scene.remove(this._farPoints);
    this._scene.remove(this._farLines);
    this._farGeometry.dispose(); this._farMaterial.dispose();
    this._farLineGeometry.dispose(); this._farLineMaterial.dispose();
    this._starGeometry.dispose(); this._starMaterial.dispose();
    this._lineGeometry.dispose(); this._lineMaterial.dispose();
    this._bgGeometry.dispose(); this._bgMaterial.dispose();
    this._backdropGeometry.dispose(); this._backdropMaterial.dispose();
  }
}
